<template>
  <div class="flex justify-between items-center h-10 shrink-0" data-recording-control="true">
    <div class="flex gap-2">
      <div :class="methodButtonClass">
        <div class="flex items-center gap-1">
          <div class="flex items-center">
            <span :class="methodTextClass">{{ $t('keys.macro.toolbar.delaySet') }}</span>
            <Tooltip :content="$t('keys.macro.toolbar.delaySetContent')">
              <Icon
                icon="Function-Wireframe-Description"
                class="mt-0.5 size-4 text-text-glass-default"
              />
            </Tooltip>
          </div>
          <input
            v-model="delayOverrideInput"
            type="text"
            inputmode="numeric"
            class="no-spinner w-13 text-center h-6 text-text-body-default rounded-full bg-white outline-none"
            :placeholder="$t('keys.macro.toolbar.input')"
            :disabled="disabled"
            @input="validateDelayOverrideInput"
            @blur="syncDelayOverrideInput"
            @keyup.enter="syncDelayOverrideInput"
          />
        </div>
      </div>

      <div :class="methodButtonClass" @click="() => updateMode(1)">
        <Icon
          :icon="methodState.index === 1 ? 'Switch-Fill-Selected' : 'Switch-Wireframe-Unselected'"
          :class="methodIconClass(1)"
        />
        <div class="flex items-center gap-1">
          <span :class="methodTextClass"> {{ $t('keys.macro.toolbar.loopCount') }} </span>
          <div class="flex items-center gap-1">
            <Button
              color="blue1"
              active
              shape="circle"
              size="icon-xs"
              :disabled="methodState.index !== 1 || disabled || methodState.num <= LOOP_MIN"
              @click.stop="decreaseLoop"
            >
              <Icon icon="Status-Wireframe-Collapse" />
            </Button>
            <input
              v-model="loopInput"
              type="text"
              inputmode="numeric"
              class="no-spinner text-center px-2 w-13 h-6 text-text-body-default rounded-full bg-white outline-none"
              :disabled="methodState.index !== 1 || disabled"
              @input="validateLoopInput"
              @blur="syncLoopInput"
              @keyup.enter="syncLoopInput"
            />
            <Button
              color="blue1"
              active
              shape="circle"
              size="icon-xs"
              :disabled="methodState.index !== 1 || disabled || methodState.num >= LOOP_MAX"
              @click.stop="increaseLoop"
            >
              <Icon icon="Function-Wireframe-Add11" />
            </Button>
          </div>
        </div>
      </div>

      <div :class="methodButtonClass" @click="() => updateMode(2)">
        <Icon
          :icon="methodState.index === 2 ? 'Switch-Fill-Selected' : 'Switch-Wireframe-Unselected'"
          :class="methodIconClass(2)"
        />
        <span :class="methodTextClass"> {{ $t('keys.macro.toolbar.togglePlayback') }} </span>
      </div>

      <div :class="methodButtonClass" @click="() => updateMode(3)">
        <Icon
          :icon="methodState.index === 3 ? 'Switch-Fill-Selected' : 'Switch-Wireframe-Unselected'"
          :class="methodIconClass(3)"
        />
        <span :class="methodTextClass"> {{ $t('keys.macro.toolbar.playOnPress') }} </span>
      </div>

      <!-- <div
        class="h-10 p-2 flex gap-1 items-center text-m-medium glass-frosted bg-neutral-light-default1 rounded-4xl cursor-pointer transition-all hover:bg-neutral-light-hover hover:text-hover"
        @click="toggleIgnoreTime"
      >
        <Icon
          :icon="methodState.ignoreTime ? 'Switch-Fill-Selected' : 'Switch-Wireframe-Unselected'"
          class="w-4 h-4"
          :class="methodState.ignoreTime ? 'text-brand-blue1' : ''"
        />
        <span>忽略延迟</span>
      </div> -->

      <Button
        color="blue2"
        size="lg"
        class="h-10 justify-between"
        :active="recording"
        :disabled="disabled"
        @click="handleToggleRecording"
      >
        <Icon icon="Switch-Fill-Selected" class="mt-px" />
        <p class="mr-1.5">
          {{ recording ? formatTime : $t('keys.macro.toolbar.startRecording') }}
        </p>
      </Button>
    </div>

    <div class="flex items-center gap-2">
      <Tooltip :content="$t('common.clear')">
        <Button
          color="glass"
          size="icon-md"
          shape="circle"
          :active="!(disabled || !canClear)"
          :disabled="disabled || !canClear"
          @click="emit('clear')"
        >
          <Icon icon="Writing-Wireframe-Clear" />
        </Button>
      </Tooltip>
      <Tooltip :content="$t('common.withdraw')">
        <Button
          color="glass"
          size="icon-md"
          shape="circle"
          :active="!(disabled || !canUndo)"
          :disabled="disabled || !canUndo"
          @click="emit('undo')"
        >
          <Icon icon="Writing-Wireframe-Undo" />
        </Button>
      </Tooltip>
      <Tooltip :content="$t('common.save')">
        <Button
          color="glass"
          size="icon-md"
          shape="circle"
          :active="!(disabled || !canSave)"
          :disabled="disabled || !canSave"
          @click="emit('save')"
        >
          <Icon icon="Writing-Wireframe-Save" />
        </Button>
      </Tooltip>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onUnmounted, reactive, ref, watch } from 'vue'
  import { Button, Icon, Tooltip } from '@rapoo/element'

  import type { MacroEditorMethod } from '../../types'

  const props = withDefaults(
    defineProps<{
      modelValue: boolean
      monitorContainer: HTMLElement
      method: MacroEditorMethod
      disabled?: boolean
      canClear?: boolean
      canUndo?: boolean
      canSave?: boolean
      delayOverride?: number | null
    }>(),
    {
      disabled: false,
      canClear: false,
      canUndo: false,
      canSave: false,
      delayOverride: null,
    },
  )

  const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void
    (e: 'update:method', value: MacroEditorMethod): void
    (e: 'update:delayOverride', value: number | null): void
    (e: 'clear'): void
    (e: 'undo'): void
    (e: 'save'): void
  }>()

  const recording = ref(props.modelValue)
  const recordingTime = ref(0)
  const recordingTimer = ref<ReturnType<typeof setInterval> | null>(null)

  const formatTime = computed(() => {
    return new Date(recordingTime.value).toISOString().slice(11, 19)
  })

  const methodState = reactive<MacroEditorMethod>({
    num: props.method.num,
    index: props.method.index,
    ignoreTime: props.method.ignoreTime,
  })

  const loopInput = ref(String(methodState.num))

  const LOOP_MIN = 1
  const LOOP_MAX = 65533
  const INPUT_MIN = 0
  const INPUT_MAX = 99999
  const DELAY_OVERRIDE_MIN = 2
  const DELAY_OVERRIDE_MAX = 30000

  const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value))

  const delayOverrideInput = ref(props.delayOverride == null ? '' : String(props.delayOverride))

  /** input 事件过滤：剔除所有非数字字符后钳到 INPUT_MIN–INPUT_MAX，返回过滤后的值 */
  const sanitizeNumericInput = (e: Event): string => {
    const input = e.target as HTMLInputElement
    let next = input.value.replace(/\D/g, '')

    if (next !== '') {
      next = String(clamp(Number.parseInt(next, 10), INPUT_MIN, INPUT_MAX))
    }

    if (next !== input.value) {
      input.value = next
    }
    return next
  }

  const validateDelayOverrideInput = (e: Event) => {
    delayOverrideInput.value = sanitizeNumericInput(e)
  }

  const syncDelayOverrideInput = () => {
    const parsed = Number.parseInt(delayOverrideInput.value, 10)

    if (Number.isNaN(parsed)) {
      emit('update:delayOverride', null)
    } else {
      const clamped = clamp(parsed, DELAY_OVERRIDE_MIN, DELAY_OVERRIDE_MAX)
      emit('update:delayOverride', clamped)
    }

    // 失焦/回车确认后清空输入框，便于再次输入；实际值已通过 emit 应用
    delayOverrideInput.value = ''
  }

  const syncMethod = () => {
    emit('update:method', {
      num: methodState.num,
      index: methodState.index,
      // ignoreTime 暂不开放用户修改，固定为 false
      ignoreTime: false,
    })
  }

  const validateLoopInput = (e: Event) => {
    loopInput.value = sanitizeNumericInput(e)
  }

  const syncLoopInput = () => {
    const parsed = Number.parseInt(loopInput.value, 10)
    methodState.num = Number.isNaN(parsed) ? LOOP_MIN : clamp(parsed, LOOP_MIN, LOOP_MAX)
    loopInput.value = String(methodState.num)
    syncMethod()
  }

  const increaseLoop = () => {
    methodState.num = clamp(methodState.num + 1, LOOP_MIN, LOOP_MAX)
    loopInput.value = String(methodState.num)
    syncMethod()
  }

  const decreaseLoop = () => {
    methodState.num = clamp(methodState.num - 1, LOOP_MIN, LOOP_MAX)
    loopInput.value = String(methodState.num)
    syncMethod()
  }

  const updateMode = (mode: 1 | 2 | 3) => {
    if (props.disabled) return

    methodState.index = mode
    syncMethod()
  }

  const stopTimer = () => {
    if (recordingTimer.value) {
      clearInterval(recordingTimer.value)
      recordingTimer.value = null
    }
    recordingTime.value = 0
  }

  const startTimer = () => {
    stopTimer()
    recordingTimer.value = setInterval(() => {
      recordingTime.value += 1000
    }, 1000)
  }

  const handleToggleRecording = () => {
    if (props.disabled) return

    emit('update:modelValue', !recording.value)
  }

  const methodButtonClass = computed(() => {
    return [
      'h-10 p-2 flex gap-1 items-center text-s-medium glass-frosted rounded-4xl transition-all',
      props.disabled
        ? 'cursor-not-allowed bg-neutral-dark-disabled'
        : 'cursor-pointer bg-neutral-light-default1 hover:bg-neutral-light-hover hover:text-hover',
    ]
  })

  const methodIconClass = computed(() => (index: number) => {
    const originalClass = 'text-black-white w-4 h-4 mt-0.5'
    if (props.disabled) {
      return originalClass + ' text-text-disabled'
    }
    if (methodState.index === index) {
      return originalClass + ' text-brand-blue1'
    }

    return originalClass
  })

  const methodTextClass = computed(() =>
    props.disabled ? 'text-text-disabled' : 'text-black-white',
  )

  watch(
    () => props.modelValue,
    (value) => {
      recording.value = value

      if (value) {
        startTimer()
        props.monitorContainer?.focus()
      } else {
        stopTimer()
      }
    },
    { immediate: true },
  )

  watch(
    () => props.method,
    (value) => {
      methodState.num = value.num
      methodState.index = value.index
      methodState.ignoreTime = value.ignoreTime
      loopInput.value = String(value.num)
    },
    { deep: true, immediate: true },
  )

  watch(
    () => props.delayOverride,
    (value) => {
      delayOverrideInput.value = value == null ? '' : String(value)
    },
  )

  onUnmounted(() => {
    if (recordingTimer.value) {
      clearInterval(recordingTimer.value)
    }
  })
</script>

<style scoped>
  .no-spinner::-webkit-outer-spin-button,
  .no-spinner::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .no-spinner {
    appearance: textfield;
    -moz-appearance: textfield;
  }
</style>
